import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, CreditCard, Sparkles, Check, Gauge, Mic, PhoneForwarded, Webhook } from "lucide-react";
import { CancelSubscriptionDialog } from "@/components/CancelSubscriptionDialog";

const PLAN_META = {
  starter_monthly: { price: "97", minutes: 250 },
  business_monthly: { price: "225", minutes: 600 },
  pro_monthly: { price: "599", minutes: 1500 },
};

export default function Settings() {
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [notifPhone, setNotifPhone] = useState("");
  const [checkingOut, setCheckingOut] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [overageEnabled, setOverageEnabled] = useState(true);
  const [limitInput, setLimitInput] = useState("");
  const [emailsInput, setEmailsInput] = useState("");
  const usageInit = useRef(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [hook, setHook] = useState(null);
  const [hookUrl, setHookUrl] = useState("");
  const [hookSaving, setHookSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [promo, setPromo] = useState("");
  useEffect(() => {
    api.get("/business").then(r => { setB(r.data); setNotifPhone(r.data.notification_phone || ""); }).catch(() => setB({ error: true }));
    api.get("/integrations/status").then(r => setIntegrations(r.data)).catch((err) => console.warn("integrations status failed:", err));
    api.get("/phone-numbers").then(r => setPhoneNumbers(r.data)).catch((err) => console.warn("phone numbers load failed:", err));
    api.get("/billing/usage").then(r => setUsage(r.data)).catch((err) => console.warn("usage load failed:", err));
    api.get("/webhooks/config").then(r => { setHook(r.data); setHookUrl(r.data.url || ""); }).catch((err) => console.warn("webhook config load failed:", err));
    api.get("/webhooks/deliveries").then(r => setDeliveries(r.data)).catch((err) => console.warn("webhook deliveries load failed:", err));
  }, []);
  useEffect(() => {
    if (usage && !usageInit.current) {
      usageInit.current = true;
      setOverageEnabled(usage.overage_enabled !== false);
      setLimitInput(usage.overage_limit_cents != null ? String(usage.overage_limit_cents / 100) : "");
      setEmailsInput((usage.notification_emails || []).join(", "));
    }
  }, [usage]);

  const saveOverage = async () => {
    try {
      const payload = {
        overage_enabled: overageEnabled,
        notification_emails: emailsInput.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (overageEnabled) payload.overage_limit_cents = limitInput ? Math.round(parseFloat(limitInput) * 100) : null;
      const r = await api.patch("/billing/overage-settings", payload);
      setUsage(r.data);
      toast.success("Overage settings saved");
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
  };
  const resumeBilling = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Call answering resumed");
      const r = await api.get("/billing/usage");
      setUsage(r.data);
    } catch (e) { toast.error(errMsg(e, "Could not resume — check your overage settings")); }
  };
  if (!b) return <div className="p-8 text-slate-500">Loading…</div>;
  if (b.error) return <div className="p-8 text-red-500" data-testid="settings-error">Couldn't load settings. Please refresh.</div>;

  const connected = !!(b.vapi_assistant_id || integrations?.twilio_number || phoneNumbers.some(p => p.voice_enabled));
  const asfaNumber = integrations?.twilio_number || phoneNumbers[0]?.number || "";

  const subscribe = async (lookupKey) => {
    setCheckingOut(lookupKey);
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin, promo_code: promo.trim() });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Checkout failed"));
      setCheckingOut(null);
    }
  };

  const savePhone = async () => {
    try {
      const r = await api.post("/phone-numbers", { number: b.phone_number });
      toast.success("Phone saved and routed to your business");
      setB({ ...b, phone_number: r.data.number });
      const nums = await api.get("/phone-numbers");
      setPhoneNumbers(nums.data);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  const goLive = async () => {
    try { await api.patch("/business", { onboarding: { ...b.onboarding, live: true } }); toast.success("You're live!"); }
    catch { toast.error("Failed"); }
  };
  const setupAI = async () => {
    setProvisioning(true);
    try {
      const r = await api.post("/vapi/provision");
      toast.success(`Your AI receptionist is ready${r.data?.number ? ` — your number: ${r.data.number}` : ""}`);
      const [b2, n2] = await Promise.all([api.get("/business"), api.get("/phone-numbers")]);
      setB(b2.data); setPhoneNumbers(n2.data);
    } catch (e) {
      toast.error(errMsg(e, "Setup didn't complete — please try again"));
    } finally { setProvisioning(false); }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
      <p className="text-slate-600 mt-1">Phone, billing, and your plan.</p>

      <div className="mt-6 grid gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="phone-status-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Phone className="w-5 h-5 text-blue-600" /></div>
            <div><div className="font-heading font-semibold text-lg">Phone &amp; AI receptionist</div><div className="text-sm text-slate-500">Your receptionist's line and connection status.</div></div>
          </div>

          {connected ? (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 p-4 rounded-xl" data-testid="phone-connected-note">
              <b>AI receptionist connected</b>
              <div className="mt-1 text-emerald-700">Your AI receptionist is ready to answer calls. SMS confirmations and reminders are active.</div>
              {asfaNumber && <div className="mt-1 text-emerald-700">Your receptionist answers on <span className="font-mono">{asfaNumber}</span>.</div>}
            </div>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 p-4 rounded-xl" data-testid="phone-not-connected-note">
              <b>Phone setup incomplete</b>
              <div className="mt-1 text-amber-700">Complete setup to activate your AI receptionist.</div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {!connected && (
              <Button data-testid="setup-receptionist-btn" onClick={setupAI} disabled={provisioning} className="rounded-xl bg-blue-600 hover:bg-blue-700">
                <Sparkles className="w-4 h-4 mr-2" />{provisioning ? "Setting up…" : "Set up my AI receptionist"}
              </Button>
            )}
            <Button data-testid="test-receptionist-btn" variant={connected ? "default" : "outline"} className={`rounded-xl ${connected ? "bg-blue-600 hover:bg-blue-700" : ""}`} onClick={() => nav("/app/test")}>
              <Mic className="w-4 h-4 mr-2" />Test my AI receptionist
            </Button>
            <Button data-testid="connect-number-btn" variant="outline" className="rounded-xl" onClick={() => setConnectOpen(true)}>
              <PhoneForwarded className="w-4 h-4 mr-2" />Connect my existing business number
            </Button>
            {connected && (
              <Button data-testid="settings-golive" onClick={goLive} variant="outline" className="rounded-xl">
                <Sparkles className="w-4 h-4 mr-2" />Go live
              </Button>
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100">
            <Label>Business phone shown to customers (confirmations &amp; texts)</Label>
            <div className="mt-1.5 flex gap-2">
              <Input data-testid="settings-phone" value={b.phone_number || ""} onChange={(e) => setB({ ...b, phone_number: e.target.value })} placeholder="+1 555 123 4567" />
              <Button data-testid="settings-phone-save" onClick={savePhone} variant="outline" className="rounded-xl">Save</Button>
            </div>
          </div>
        </div>

        <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
          <DialogContent data-testid="connect-number-dialog">
            <DialogHeader><DialogTitle>Connect your existing business number</DialogTitle></DialogHeader>
            {connected && asfaNumber ? (
              <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                <li>On your business phone, turn on call forwarding to <span className="font-mono font-medium">{asfaNumber}</span>. Most Canadian carriers: dial <code className="bg-slate-100 px-1 rounded">*72</code> then that number.</li>
                <li>Prefer the AI to answer only when you can't? Ask your carrier for <b>busy/no-answer forwarding</b> instead.</li>
                <li>Call your own number to test — your AI receptionist should pick up.</li>
              </ol>
            ) : (
              <p className="text-sm text-slate-600">First click <b>Set up my AI receptionist</b> so we can assign your receptionist's number — then you'll forward your existing line to it.</p>
            )}
            <p className="text-xs text-slate-400">Want your number fully moved over (no forwarding)? Contact support about porting it.</p>
          </DialogContent>
        </Dialog>

        {usage?.subscribed && !usage?.error && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="usage-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Gauge className="w-5 h-5 text-emerald-600" /></div>
              <div><div className="font-heading font-semibold text-lg">Usage this billing period</div>
                <div className="text-sm text-slate-500" data-testid="usage-period">{new Date(usage.period_start).toLocaleDateString()} – {new Date(usage.period_end).toLocaleDateString()}</div></div>
            </div>
            <div className="flex items-end justify-between text-sm flex-wrap gap-x-3 gap-y-1">
              <div><span className="text-2xl font-bold" data-testid="usage-minutes-used">{usage.used_minutes}</span> <span className="text-slate-500">/ {usage.included_minutes} minutes used</span></div>
              <div className="text-slate-500" data-testid="usage-minutes-remaining">{usage.remaining_minutes} minutes remaining</div>
            </div>
            <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${usage.percent_used >= 100 ? "bg-red-500" : usage.percent_used >= 90 ? "bg-orange-500" : usage.percent_used >= 75 ? "bg-amber-400" : "bg-emerald-500"}`}
                   style={{ width: `${Math.min(100, usage.percent_used)}%` }} data-testid="usage-bar" />
            </div>
            {usage.percent_used >= 100 && (
              <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700" data-testid="usage-warning-100">
                You've used all {usage.included_minutes} included minutes this period. Additional minutes are billed at your plan's overage rate of <b>${(usage.overage_rate_cents / 100).toFixed(2)}/min CAD</b>.
              </div>
            )}
            {usage.percent_used >= 80 && usage.percent_used < 100 && (
              <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-800" data-testid="usage-warning-80">
                Heads up — you've used {Math.round(usage.percent_used)}% of your included minutes. After {usage.included_minutes} minutes, overage is billed at ${(usage.overage_rate_cents / 100).toFixed(2)}/min CAD.
              </div>
            )}
            <div className="mt-2 text-sm text-slate-600">
              Overage: <b data-testid="usage-overage-minutes">{usage.overage_minutes} min</b> at ${(usage.overage_rate_cents / 100).toFixed(2)}/min CAD
              {usage.estimated_overage_cents > 0 && <> — estimated <b data-testid="usage-est-overage">${(usage.estimated_overage_cents / 100).toFixed(2)}</b> this period</>}
            </div>
            {usage.paused && (
              <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700" data-testid="usage-paused-note">
                Call answering is paused — ASFA is not answering your line right now.
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" data-testid="overage-enabled-toggle" checked={overageEnabled} onChange={(e) => setOverageEnabled(e.target.checked)} />
                Bill overage after included minutes (${(usage.overage_rate_cents / 100).toFixed(2)}/min CAD on your plan)
              </label>
              {overageEnabled && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-sm">Monthly overage spending limit (CAD)</Label>
                  <Input data-testid="overage-limit-input" type="number" min="0" step="1" placeholder="No limit" value={limitInput} onChange={(e) => setLimitInput(e.target.value)} className="w-32" />
                  <span className="text-xs text-slate-500">Answering pauses at this amount. Empty = no limit.</span>
                </div>
              )}
              {!overageEnabled && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2">With overage off, answering pauses at 100% of included minutes until your next billing period.</p>}
              <div>
                <Label className="text-sm">Alert emails (comma-separated)</Label>
                <Input data-testid="alert-emails-input" value={emailsInput} onChange={(e) => setEmailsInput(e.target.value)} placeholder="you@business.com" className="mt-1.5" />
                <p className="text-xs text-slate-500 mt-1">Usage alerts at 75%, 90% and 100% go to these emails and your business phone by SMS.</p>
              </div>
              <div className="flex gap-2">
                <Button data-testid="overage-save-btn" size="sm" onClick={saveOverage} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save overage settings</Button>
                {usage.paused && <Button data-testid="billing-resume-btn" size="sm" variant="outline" onClick={resumeBilling} className="rounded-xl">Resume answering</Button>}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="notifications-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Phone className="w-5 h-5 text-amber-600" /></div>
            <div><div className="font-heading font-semibold text-lg">Notifications & texting</div>
            <div className="text-sm text-slate-500">Get pinged the moment money comes in, and let customers text your number.</div></div>
          </div>
          <Label className="text-xs text-slate-500">Your mobile for instant alerts (new bookings, quote requests)</Label>
          <div className="flex gap-2 mt-1 max-w-sm">
            <Input data-testid="notification-phone-input" value={notifPhone} onChange={(e) => setNotifPhone(e.target.value)} placeholder="+1 555 123 4567" />
            <Button data-testid="notification-phone-save" variant="outline" className="rounded-xl" onClick={async () => {
              try { const r = await api.patch("/business", { notification_phone: notifPhone.trim() }); setB(r.data); toast.success("Alert phone saved"); }
              catch (e) { toast.error(errMsg(e, "Could not save")); }
            }}>Save</Button>
          </div>
          <div className="mt-4 space-y-3">
            {[
              { key: "alerts_enabled", label: "Instant owner alerts", desc: "Email + SMS the moment a booking or quote request lands.", def: true },
              { key: "sms_ai_enabled", label: "Two-way AI texting", desc: "Customers can text your number; the receptionist replies and books by text.", def: true },
              { key: "textback_enabled", label: "Missed-call text-back", desc: "If a call can't be answered (e.g. minutes used up), the caller instantly gets a text inviting them to text instead.", def: true },
              { key: "confirmations_enabled", label: "24h confirm-by-text", desc: "Customers get a 'Reply C to confirm' text the day before their appointment. Off by default.", def: false },
            ].map(t => (
              <label key={t.key} className="flex items-start gap-2.5 text-sm cursor-pointer" data-testid={`toggle-${t.key}`}>
                <input type="checkbox" className="mt-1" checked={b[t.key] === undefined ? t.def : !!b[t.key]}
                  onChange={async (e) => {
                    try { const r = await api.patch("/business", { [t.key]: e.target.checked }); setB(r.data); toast.success("Saved"); }
                    catch (err) { toast.error(errMsg(err, "Could not save")); }
                  }} />
                <span><b className="text-slate-800">{t.label}</b><span className="text-slate-500"> — {t.desc}</span></span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="chatbot-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Sparkles className="w-5 h-5 text-blue-600" /></div>
            <div><div className="font-heading font-semibold text-lg">Website chatbot</div>
            <div className="text-sm text-slate-500">Let website visitors chat with your AI receptionist — it answers questions and books appointments using your business knowledge.</div></div>
          </div>
          <Label className="text-xs text-slate-500">Paste this snippet before the closing &lt;/body&gt; tag of your website</Label>
          <div className="mt-1 flex gap-2">
            <code data-testid="chatbot-snippet" className="flex-1 text-xs bg-slate-950 text-emerald-300 rounded-xl px-3 py-2.5 overflow-x-auto whitespace-nowrap select-all">
              {`<script src="${window.location.origin}/widget.js" data-business="${b.id}" defer></script>`}
            </code>
            <Button data-testid="chatbot-copy-btn" variant="outline" className="rounded-xl shrink-0"
              onClick={() => { navigator.clipboard.writeText(`<script src="${window.location.origin}/widget.js" data-business="${b.id}" defer></script>`); toast.success("Snippet copied"); }}>
              Copy
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-400">Works on any website — WordPress, Wix, Squarespace, or hand-coded. The chatbot activates automatically while your plan is active.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="crm-webhook-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Webhook className="w-5 h-5 text-emerald-600" /></div>
            <div><div className="font-heading font-semibold text-lg">CRM & integrations</div>
            <div className="text-sm text-slate-500">Push bookings, new customers, cancellations and call summaries to your CRM — works with Zapier, Make, GoHighLevel, HubSpot, or any webhook URL.</div></div>
          </div>
          <Label className="text-xs text-slate-500">Webhook URL</Label>
          <div className="flex gap-2 mt-1 flex-wrap">
            <Input data-testid="webhook-url-input" className="flex-1 min-w-[220px]" value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/…" />
            <Button data-testid="webhook-save-btn" variant="outline" className="rounded-xl" disabled={hookSaving}
              onClick={async () => {
                setHookSaving(true);
                try {
                  const r = await api.put("/webhooks/config", { url: hookUrl.trim(), enabled: true });
                  setHook(r.data);
                  toast.success(r.data.url ? "Webhook saved — events will be posted there" : "Webhook cleared");
                } catch (e) { toast.error(errMsg(e, "Could not save webhook")); }
                finally { setHookSaving(false); }
              }}>{hookSaving ? "Saving…" : "Save"}</Button>
            {hook?.url && (
              <Button data-testid="webhook-test-btn" variant="outline" className="rounded-xl"
                onClick={async () => {
                  try {
                    const r = await api.post("/webhooks/test");
                    if (r.data?.ok) toast.success(`Test event delivered (HTTP ${r.data.status})`);
                    else toast.error(`Test failed${r.data?.status ? ` — HTTP ${r.data.status}` : ""}${r.data?.error ? `: ${r.data.error.slice(0, 80)}` : ""}`);
                    const d = await api.get("/webhooks/deliveries");
                    setDeliveries(d.data);
                  } catch (e) { toast.error(errMsg(e, "Test failed")); }
                }}>Send test</Button>
            )}
          </div>
          {hook?.url && hook?.secret && (
            <p className="mt-2 text-xs text-slate-500">Signing secret (verify the <code>X-ASFA-Signature</code> header): <code className="bg-slate-100 px-1.5 py-0.5 rounded select-all" data-testid="webhook-secret">{hook.secret}</code></p>
          )}
          <p className="mt-2 text-xs text-slate-400" data-testid="webhook-events-list">Events sent: customer.created · appointment.booked · appointment.rescheduled · appointment.cancelled · call.completed</p>
          {deliveries.length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="webhook-deliveries">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Recent deliveries</div>
              {deliveries.slice(0, 5).map(d => (
                <div key={d.id} className="flex justify-between gap-2 text-xs py-1">
                  <span className="text-slate-600">{d.event} <span className="text-slate-400">· {new Date(d.created_at).toLocaleString()}</span></span>
                  <span className={d.ok ? "text-emerald-600" : "text-red-600"}>{d.ok ? `Delivered (${d.status})` : `Failed${d.status ? ` (${d.status})` : ""}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center"><CreditCard className="w-5 h-5 text-purple-600" /></div>
            <div><div className="font-heading font-semibold text-lg">Billing</div>
            <div className="text-sm text-slate-500">
              {b.subscription_status === "active" ? <>You're on the <b className="capitalize">{(b.plan || "paid").replace("_monthly", "")}</b> plan.</> : "No active plan — pick one below to activate your receptionist. A one-time $100 setup fee applies."}
            </div></div>
          </div>
          <div className="text-sm text-slate-600">Status: <b className="text-slate-950 capitalize" data-testid="billing-status">{b.subscription_status}</b></div>
          {b.subscription_status === "active" && (PLAN_META[b.plan] || PLAN_META[`${b.plan}_monthly`]) && (
            <div className="mt-1 text-sm text-slate-500" data-testid="billing-plan-details">
              ${(PLAN_META[b.plan] || PLAN_META[`${b.plan}_monthly`]).price}/mo CAD · {(PLAN_META[b.plan] || PLAN_META[`${b.plan}_monthly`]).minutes.toLocaleString()} minutes included
              {usage?.period_end && <> · renews {new Date(usage.period_end).toLocaleDateString()}</>}
            </div>
          )}
          {b.subscription_status === "active" && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <Button data-testid="manage-billing-btn" variant="outline" className="rounded-xl"
                onClick={async () => {
                  try {
                    const r = await api.post("/payments/portal", { origin_url: window.location.origin });
                    window.location.href = r.data.portal_url;
                  } catch (e) { toast.error(errMsg(e, "Could not open billing portal")); }
                }}>
                Manage subscription
              </Button>
              <Button data-testid="cancel-subscription-btn" variant="ghost"
                className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setCancelOpen(true)}>
                Cancel subscription
              </Button>
            </div>
          )}
          {b.subscription_status !== "active" && b.trial_end && (
            <div className="text-sm text-slate-600 mt-1">Trial ends: <b className="text-slate-950">{new Date(b.trial_end).toLocaleDateString()}</b></div>
          )}

          {b.subscription_status !== "active" && (
            <div className="mt-4 flex items-center gap-2 max-w-sm">
              <Input data-testid="promo-code-input" placeholder="Promo code (optional)" value={promo} onChange={(e) => setPromo(e.target.value)} className="h-9 text-sm" />
              {promo.trim().toUpperCase() === "LAUNCH50" && <span className="text-xs text-emerald-600 font-medium whitespace-nowrap" data-testid="promo-hint">Setup fee waived + 50% off month 1</span>}
            </div>
          )}

          {b.subscription_status !== "active" && (
            <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="plan-cards">
              {[
                { key: "starter_monthly", name: "Starter", price: 97, bestFor: "For businesses that mainly need call answering and lead capture.", feat: ["250 call minutes/mo", "1 phone number", "Booking + SMS confirmations", "FAQs & message taking", "Call transcripts + basic dashboard", "Overage $0.35/min after included minutes"] },
                { key: "business_monthly", name: "Business", price: 225, bestFor: "For businesses that need full appointment management and follow-ups.", feat: ["600 call minutes/mo", "Book, cancel & reschedule", "Customer info capture + staff transfers", "SMS confirmations & reminders", "Google review automation", "Custom business knowledge", "Overage $0.25/min"], hot: true },
              ].map(p => (
                <div key={p.key} className={`rounded-2xl border p-4 ${p.hot ? "border-blue-500 bg-blue-50/50" : "border-slate-200"}`} data-testid={`plan-${p.key}`}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{p.name}</div>
                  <div className="mt-1 text-2xl font-bold font-heading">${p.price}<span className="text-sm font-normal text-slate-500">/mo</span></div>
                  <p className="mt-1.5 text-xs text-slate-500 leading-snug" data-testid={`best-for-${p.key}`}>{p.bestFor}</p>
                  <ul className="mt-3 space-y-1 text-xs text-slate-600">
                    {p.feat.map(f => <li key={f} className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />{f}</li>)}
                  </ul>
                  <Button data-testid={`subscribe-${p.key}`} disabled={checkingOut === p.key}
                    onClick={() => subscribe(p.key)}
                    className={`mt-4 w-full rounded-xl ${p.hot ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                    variant={p.hot ? "default" : "outline"}>
                    {checkingOut === p.key ? "Redirecting…" : "Subscribe"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CancelSubscriptionDialog open={cancelOpen} onClose={() => setCancelOpen(false)} />
    </div>
  );
}
